import 'webrtc-adapter';
import { EventEmitter } from 'events';
import { SDPInfo, MediaInfo } from 'semantic-sdp';

import PeerStats from '../webrtc/PeerStats';
import Debug from '@/debug';
const debug = Debug('bbc:PeerConnection');

export default class PeerConnection extends EventEmitter {
    /**
     * 
     * @param {String} remotePeerId
     * @param {Boolean} makeOffer (do we make the offer?)
     * @param {Object} userData
     * @param {Array} iceServers
     * @param {Object} mediaTracks {audio, video}
     */
    constructor(remotePeerId, makeOffer = false, userData = {}, iceServers = [], mediaTracks = {}, useVideo = false) {
        debug('constructor()', {
            remotePeerId,
            makeOffer,
            userData,
            iceServers,
            mediaTracks,
            useVideo
        });
        super();

        this.remotePeerId = remotePeerId;   // this is the peer we are establishing a connection with
        this.makeOffer = makeOffer;
        this.userData = userData;
        this.iceServers = iceServers;
        this.stream = new MediaStream();
        this.peerStats = new PeerStats();

        this.pc = new RTCPeerConnection({
            iceServers: this.iceServers
        });

        this.useVideo = useVideo;
        this.audioTransceiver = null;
        this.videoTransceiver = null;
        
        if (mediaTracks) {
            this.audioTrack = mediaTracks.audio;
            this.videoTrack = mediaTracks.video;
        }

        this._initialiseTransceivers();
        this._initialiseTracks();
        this._initialiseEventListeners();

        this.statsTimer = null;
    }

    _initialiseTransceivers() {
        if (this.makeOffer) {    // only create the transceivers when making Offer
            this.audioTransceiver = this.pc.addTransceiver('audio', { direction: 'sendrecv' });
            debug('_initialiseTransceivers()', 'Added audio transceiver');

            if (this.useVideo) {
                this.videoTransceiver = this.pc.addTransceiver('video', { direction: 'sendrecv' });
                debug('_initialiseTransceivers()', 'Added video transceiver');
            }
        }
    }

    _initialiseTracks() {
        debug('_initialiseTracks()');
        if (this.audioTrack) this.replaceAudioTrack(this.audioTrack);
        if (this.useVideo && this.videoTrack) this.replaceVideoTrack(this.videoTrack);
    }

    _initialiseEventListeners() {
        debug('_initialiseEventListeners()');
        this.pc.addEventListener('icecandidate', this.onIceCandidate.bind(this));
        this.pc.addEventListener('icecandidateerror', this.onIceCandidateError.bind(this));
        this.pc.addEventListener('icegatheringstatechange', this.onIceGatheringStateChanged.bind(this));
        this.pc.addEventListener('iceconnectionstatechange', this.onIceConnectionStateChanged.bind(this));
        this.pc.addEventListener('negotiationneeded', this.onNegotiationNeeded.bind(this));
        this.pc.addEventListener('signalingstatechange', this.onSignalingStateChanged.bind(this));
        this.pc.addEventListener('track', this.onTrack.bind(this));
        this.pc.addEventListener('connectionstatechange', this.onConnectionStateChanged.bind(this));
    }

    close() {
        this.pc.close();
    }

    async updatePeerStats() {
        if (!this.pc) return;

        try {
            let data = await this.pc.getReceivers()[0].getStats();
            data.forEach((item) => {
                if (item.type === 'inbound-rtp' && !item.isRemote) {
                    let result = this.peerStats.update(item);
                    if (result) {
                        this.emit('STATS', { target: this });
                    }
                }
            });

            setTimeout(this.updatePeerStats.bind(this), 1000);
        }
        catch (err) {
            console.error(`Failed to update peer stats. ${this.remotePeerId}`, err);
        }
    }

    /**
     * Set remote SessionDescription, received via signal server
     * @param {Object} description 
     */
    async receiveSessionDescription(description) {
        debug('receiveSessionDescription()', description);
        let remoteDescription;

        try {
            remoteDescription = new RTCSessionDescription(description);
            await this.pc.setRemoteDescription(remoteDescription);
        }
        catch (err) {
            console.error('PeerConnection: Failed to set remote description', err);
        }

        if (remoteDescription.type === 'offer') {
            this.createAnswer();
        }
    }

    /**
     * Add ICE candidate, received via signal server
     * @param {Object} candidate 
     */
    async receiveIceCandidate(candidate) {
        debug('receiveIceCandidate()', candidate);
        try {
            await this.pc.addIceCandidate(new RTCIceCandidate(candidate));
        }
        catch (err) {
            console.error('PeerConnection: Failed to add Ice candidate', err);
        }
    }

    /**
     * Replace current outgoing audio track with a new one
     * @param {Object} track 
     */
    replaceAudioTrack(track) {
        debug('replaceAudioTrack()', track);
        try {
            this.audioTrack = track;
            if (this.audioTransceiver) {
                debug('replaceAudioTrack()', 'audioTransceiver.sender.replaceTrack');
                this.audioTransceiver.sender.replaceTrack(this.audioTrack);
                debug('audioTransceiver.direction', this.audioTransceiver.direction);
                if (this.audioTransceiver.direction === 'inactive') this.audioTransceiver.direction = 'sendonly';
                if (this.audioTransceiver.direction === 'recvonly') this.audioTransceiver.direction = 'sendrecv';
            }
            else {
                debug('replaceAudioTrack()', 'pc.addTrack');
                this.pc.addTrack(this.audioTrack);
            }
        }
        catch (err) {
            console.error('PeerConnection: Failed to replace audio track', err);
        }
    }

    /**
     * Replace current outgoing video track with a new one
     * @param {Object} track 
     */
    replaceVideoTrack(track) {
        debug('replaceVideoTrack()', track);
        try {
            this.videoTrack = track;
            if (this.videoTransceiver) {
                debug('replaceVideoTrack()', 'videoTransceiver.sender.replaceTrack');
                this.videoTransceiver.sender.replaceTrack(this.videoTrack);
                //if (this.videoTransceiver.direction === 'inactive') this.videoTransceiver.direction = 'sendonly';
                //if (this.videoTransceiver.direction === 'recvonly') this.videoTransceiver.direction = 'sendrecv';
            }
            else {
                debug('replaceVideoTrack()', 'pc.addTrack');
                this.pc.addTrack(this.videoTrack);
            }
        }
        catch (err) {
            console.error('PeerConnection: Failed to replace video track', err);
        }
    }

    /**
     * Internal functions
     */

    // Only offer OPUS at 64kbps
    mungeSDP(description) {
        const SDP = SDPInfo.parse(description.sdp);

        let audio = SDP.getMedia('audio');
        let opus = audio.getCodec('opus');

        let newAudio = new MediaInfo(audio.getId(), 'audio');
        newAudio.setBitrate(64);
        newAudio.addCodec(opus);
        SDP.replaceMedia(newAudio);

        description.sdp = SDP.toString();
        return description;
    }

    async createOffer() {
        debug('createOffer()');
        try {
            let description = await this.pc.createOffer();
            description = this.mungeSDP(description);
            this.setLocalDescription(description);
        }
        catch (err) {
            console.error('PeerConnection: Failed to create offer', err);
        }
    }

    async createAnswer() {
        debug('createAnswer()');
        try {
            let description = await this.pc.createAnswer();
            description = this.mungeSDP(description);
            this.setLocalDescription(description);
        }
        catch (err) {
            console.error('PeerConnection: Failed to create answer', err);
        }
    }

    async setLocalDescription(description) {
        debug('setLocalDescription()', description);
        try {
            await this.pc.setLocalDescription(description);
            this.emit('RELAY_SESSION_DESCRIPTION', {
                'peer_id': this.remotePeerId,
                'session_description': description
            });
        }
        catch (err) {
            console.error('PeerConnection: Failed to set local description', err);
        }
    }

    async getPeerStats() {
        try {
            let stats = await this.pc.getStats();
            console.log("PeerStats:");
            stats.forEach((item, key) => {
                console.log(key, item);
            })
        }
        catch (err) {
            console.error('PeerConnection: Failed to get peer stats');
        }
    }
    /**
     * RTCPeerConnection event handlers - private functions
     **/

    onIceCandidate(event) {
        debug('onIceCandidate()', event);
        if (event.candidate) {
            // Send the candidate to the remote peer
            this.emit('RELAY_ICE_CANDIDATE', {
                'peer_id': this.remotePeerId,
                'ice_candidate': event.candidate
            });
        }
    }

    onIceCandidateError(event) {
        console.error('onIceCandidateError', event);
    }

    onIceGatheringStateChanged(event) {
        debug('onIceGatheringStateChanged()', event);
        let connection = event.target;

        switch (connection.iceGatheringState) {
            case "gathering":
                /* collection of candidates has begun */
                break;
            case "complete":
                /* collection of candidates is finished */
                break;
        }
    }

    onIceConnectionStateChanged(event) {
        debug('onIceConnectionStateChanged()', event);
        let connection = event.target;

        switch (connection.iceConnectionState) {
            case 'new':
            case 'checking':
            case 'connecting':
            case 'complete':
                // console.log('iceConnectionState', connection.iceConnectionState);
                break;
            case "failed":
            case "disconnected":
            case "closed":
                // Handle the failure
                console.warn('iceConnectionState', connection.iceConnectionState);
                break;
        }
    }

    onNegotiationNeeded() {
        debug('onNegotiationNeeded()');
        // console.log('onNegotiationNeeded, creating offer');
        if (this.makeOffer) {
            // make the offer
            this.createOffer();
        }
    }

    onSignalingStateChanged(event) {
        debug('onSignalingStateChanged', event);
    }

    onTrack(event) {
        debug('onTrack', event);
        // New media track has been added to WebRTC stream
        let track = event.track;        // this is the actual MediaTrack
        this.stream.addTrack(track);
        this.emit('MEDIA', { target: this });
    }

    onConnectionStateChanged(event) {
        let connection = event.target;
        debug('onConnectionStateChanged', connection.connectionState);
        switch (connection.connectionState) {
            case "connected":
                this._getTransceivers();
                this.updatePeerStats();
                // The connection has become fully connected
                this.emit('CONNECTED', { target: this });
                break;
            case "disconnected":
            case "failed":
                // One or more transports has terminated unexpectedly or in an error
                this.emit('DISCONNECTED');
                break;
            case "closed":
                // The connection has been closed
                this.emit('CLOSED');
                break;
        }
    }

    _getTransceivers() {
        let transceivers = this.pc.getTransceivers();
        transceivers.forEach(t => {
            if (t.receiver.track.kind === 'audio') {
                this.audioTransceiver = t;
                this.audioTransceiver.direction = 'sendrecv';
            }
            if (t.receiver.track.kind === 'video') {
                this.videoTransceiver = t;
                this.videoTransceiver.direction = 'sendrecv';
            }
        });
    }
}